import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import GrindrodLogo from "../components/GrindrodLogo";
import RemarksPanel from "../components/RemarksPanel";
import UnifiedTimeline from "../components/UnifiedTimeline";
import HatchOverlayCard from "../components/HatchOverlayCard";
import { toast } from "sonner";
import { Printer, ArrowLeft, DownloadSimple } from "@phosphor-icons/react";
import { fmtDuration } from "../lib/duration";

function fmtDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}
function fmtDT(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString(undefined, { hour12: false });
}
function fmtTime(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
}
function fmtMins(m) {
    return fmtDuration(m);
}
function fmtHM(m) {
    return fmtDuration(m);
}

function csvEscape(v) {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
}

function downloadCsv(filename, rows) {
    const csv = rows.map((r) => r.map(csvEscape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

export default function Certificate() {
    const { id } = useParams();
    const nav = useNavigate();
    const [vessel, setVessel] = useState(null);
    const [metrics, setMetrics] = useState(null);
    const [reports, setReports] = useState([]);
    const [full, setFull] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const [v, m, r, f] = await Promise.all([
                    api.get(`/vessels/${id}`),
                    api.get(`/vessels/${id}/metrics`),
                    api.get(`/vessels/${id}/reports`),
                    api.get(`/vessels/${id}/full-report`),
                ]);
                setVessel(v.data);
                setMetrics(m.data);
                setReports(r.data);
                setFull(f.data);
            } catch (e) {
                toast.error(formatApiError(e));
            }
        })();
    }, [id]);

    const exportCsv = () => {
        if (!vessel) return;
        const _loadedTotal = vessel.total_tons_loaded
            ?? (metrics?.latest_tob != null ? metrics.latest_tob : reports.reduce((s, r) => s + r.tons_loaded, 0));
        const _delayTotal = reports.reduce((s, r) => s + r.total_delay_minutes, 0);
        const _actualStart = vessel.actual_start_datetime || vessel.start_datetime;
        const _endMs = vessel.completed_at ? new Date(vessel.completed_at).getTime() : Date.now();
        const _turnMs = Math.max(0, _endMs - new Date(_actualStart).getTime());
        const _turnH = _turnMs / 3600000;
        const _turnMinTotal = Math.floor(_turnMs / 60000);
        const _days = Math.floor(_turnMinTotal / 1440);
        const _hours = Math.floor((_turnMinTotal % 1440) / 60);
        const _mins = _turnMinTotal % 60;
        const _turnLabel = `${_days > 0 ? _days + "d " : ""}${_hours}h ${_mins}m`;
        const _overallRate = _turnH > 0 ? _loadedTotal / _turnH : 0;
        const rows = [];
        rows.push(["Grindrod Terminals — Richards Bay Division"]);
        rows.push(["Vessel Performance Report"]);
        rows.push([]);
        rows.push(["Vessel", vessel.vessel_name]);
        rows.push(["Client", vessel.client_name]);
        rows.push(["Customer", vessel.customer || ""]);
        rows.push(["Product", vessel.product]);
        rows.push(["Tonnage", vessel.tonnage]);
        rows.push(["Planned start", vessel.planned_start_datetime ? fmtDT(vessel.planned_start_datetime) : "—"]);
        rows.push(["Actual start", fmtDT(_actualStart)]);
        rows.push(["Completed", vessel.completed_at ? fmtDT(vessel.completed_at) : "In progress"]);
        rows.push(["Turnaround time", _turnLabel]);
        // Productive / Unproductive
        const _unprodMin = Math.round((metrics.all_delays || []).reduce((s, d) => s + (Number(d.duration_minutes) || 0), 0));
        const _prodMin = Math.max(0, _turnMinTotal - _unprodMin);
        rows.push(["Productive time", fmtHM(_prodMin)]);
        rows.push(["Unproductive time", fmtHM(_unprodMin)]);
        rows.push(["Total tons loaded", Math.round(_loadedTotal)]);
        rows.push(["Overall loading rate (t/h)", Math.round(_overallRate)]);
        rows.push(["Avg flow rate (t/h, reported windows)", Math.round(metrics.avg_flow_rate)]);
        rows.push(["Total delay (min)", Math.round(_delayTotal)]);
        rows.push([]);
        rows.push(["REPORTS"]);
        rows.push(["Date", "Window start", "Window end", "TOB", "Tons loaded", "Flow rate t/h", "Delays count", "Delay minutes", "Supervisor"]);
        reports.forEach((r) => {
            rows.push([
                fmtDate(r.window_start),
                fmtTime(r.window_start),
                fmtTime(r.window_end),
                r.tob,
                r.tons_loaded,
                Math.round(r.flow_rate_tph),
                r.delays.length,
                Math.round(r.total_delay_minutes),
                r.submitted_by_name,
            ]);
        });
        rows.push([]);
        rows.push(["DELAYS"]);
        rows.push(["Date", "Start", "End", "Duration (min)", "Reason", "Actions taken", "Location", "Reported by"]);
        (metrics.all_delays || []).forEach((d) => {
            rows.push([
                fmtDate(d.start_time),
                fmtTime(d.start_time),
                fmtTime(d.end_time),
                Math.round(d.duration_minutes),
                d.reason,
                d.actions_taken || "",
                d.location || "",
                d.reported_by,
            ]);
        });
        const filename = `${vessel.vessel_name.replace(/[^a-z0-9]+/gi, "_")}_performance.csv`;
        downloadCsv(filename, rows);
        toast.success("CSV exported");
    };

    if (!vessel || !metrics) {
        return <div className="p-10 text-slate-500" data-testid="certificate-loading">Loading…</div>;
    }

    const loadedTotal = vessel.total_tons_loaded
        ?? (metrics.latest_tob != null ? metrics.latest_tob : reports.reduce((s, r) => s + r.tons_loaded, 0));
    const delayTotal = reports.reduce((s, r) => s + r.total_delay_minutes, 0);
    const delays = metrics.all_delays || [];

    // Vessel start references
    const plannedStart = vessel.planned_start_datetime || null;
    const actualStart = vessel.actual_start_datetime || vessel.start_datetime;
    const lateStartMin = plannedStart
        ? Math.max(0, (new Date(actualStart).getTime() - new Date(plannedStart).getTime()) / 60000)
        : 0;

    // Turnaround time + overall loading rate
    const startMs = new Date(actualStart).getTime();
    const endMs = vessel.completed_at ? new Date(vessel.completed_at).getTime() : Date.now();
    const turnaroundMs = Math.max(0, endMs - startMs);
    const turnaroundHours = turnaroundMs / 3600000;
    const turnaroundLabel = (() => {
        const totalMin = Math.floor(turnaroundMs / 60000);
        const days = Math.floor(totalMin / 1440);
        const hours = Math.floor((totalMin % 1440) / 60);
        const mins = totalMin % 60;
        const parts = [];
        if (days > 0) parts.push(`${days}d`);
        if (hours > 0 || days > 0) parts.push(`${hours}h`);
        parts.push(`${mins}m`);
        return parts.join(" ");
    })();
    const overallLoadingRate = turnaroundHours > 0 ? loadedTotal / turnaroundHours : 0;

    // Productive vs unproductive time (in minutes).
    // Unproductive = total delay minutes from the metrics all_delays (deduped source of truth).
    // Productive = turnaround minutes - unproductive minutes (clamped to ≥ 0).
    const turnaroundMin = Math.floor(turnaroundMs / 60000);
    const unproductiveMin = Math.round(
        (metrics.all_delays || []).reduce((s, d) => s + (Number(d.duration_minutes) || 0), 0)
    );
    const productiveMin = Math.max(0, turnaroundMin - unproductiveMin);
    const productivePct = turnaroundMin > 0 ? Math.round((productiveMin / turnaroundMin) * 100) : 0;

    return (
        <div className="min-h-screen bg-[var(--surface-alt)] pb-10" data-testid="certificate-page">
            {/* Print toolbar - hidden when printing */}
            <div className="print:hidden bg-white border-b border-slate-200 sticky top-0 z-10">
                <div className="max-w-[1100px] mx-auto px-4 md:px-6 py-3 flex items-center justify-between gap-2 flex-wrap">
                    <button onClick={() => nav(-1)} className="btn-outline inline-flex items-center gap-2" data-testid="cert-back-btn">
                        <ArrowLeft size={14} /> Back
                    </button>
                    <div className="flex gap-2 flex-wrap">
                        <button onClick={exportCsv} className="btn-outline inline-flex items-center gap-2" data-testid="cert-csv-btn">
                            <DownloadSimple size={14} /> Export CSV
                        </button>
                        <button onClick={() => window.print()} className="btn-primary inline-flex items-center gap-2" data-testid="cert-print-btn">
                            <Printer size={14} /> Print / Save as PDF
                        </button>
                    </div>
                </div>
            </div>

            {/* The printable sheet */}
            <div className="max-w-[1100px] mx-auto bg-white shadow-sm mt-6 print:mt-0 print:shadow-none print:max-w-full overflow-hidden">
                {/* Sheet header */}
                <div className="px-5 md:px-10 pt-8 md:pt-10 pb-6 border-b-4 border-[var(--grindrod-red)]">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <GrindrodLogo size="xl" subtitle="RICHARDS BAY DIVISION" />
                        <div className="text-right min-w-0">
                            <div className="uppercase-label text-[var(--grindrod-red)]">Vessel Performance Certificate</div>
                            <div className="font-display font-black text-[var(--grindrod-navy)] text-2xl md:text-3xl mt-1 break-words">
                                {vessel.vessel_name}
                            </div>
                            <div className="text-sm text-slate-500 mt-1">
                                Issued {fmtDate(new Date().toISOString())} · Ref {vessel.id.slice(0, 8).toUpperCase()}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Summary */}
                <div className="px-5 md:px-10 py-6 md:py-8 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 border-b border-slate-200">
                    <Cell label="Client" value={vessel.client_name} />
                    <Cell label="Customer" value={vessel.customer || "—"} />
                    <Cell label="Product" value={vessel.product} />
                    <Cell label="Tonnage" value={`${vessel.tonnage.toLocaleString()} t`} />
                    <Cell
                        label="Planned start"
                        value={plannedStart ? fmtDT(plannedStart) : "—"}
                    />
                    <Cell
                        label="Actual start"
                        value={fmtDT(actualStart)}
                        accent={lateStartMin > 15 ? "red" : null}
                        sub={lateStartMin > 15 ? `${Math.round(lateStartMin / 60 * 10) / 10} h late` : (lateStartMin > 0 ? "On time" : null)}
                    />
                    <Cell label="Completed" value={vessel.completed_at ? fmtDT(vessel.completed_at) : "In progress"} />
                    <Cell label="Turnaround" value={turnaroundLabel} sub={`${turnaroundHours.toFixed(2)} h total`} />
                    <Cell
                        label="Productive time"
                        value={fmtHM(productiveMin)}
                        sub={`${productivePct}% of turnaround`}
                        accent={productivePct >= 80 ? "green" : (productivePct < 60 ? "red" : null)}
                    />
                    <Cell
                        label="Unproductive time"
                        value={fmtHM(unproductiveMin)}
                        sub={unproductiveMin > 0 ? "total delays + pauses" : "no delays"}
                        accent={unproductiveMin > 0 ? "red" : "green"}
                    />
                    <Cell label="Tons loaded" value={`${Math.round(loadedTotal).toLocaleString()} t`} />
                    <Cell label="Overall loading rate" value={`${Math.round(overallLoadingRate).toLocaleString()} t/h`} sub="tons ÷ turnaround" />
                    <Cell label="Avg flow rate (windows)" value={`${Math.round(metrics.avg_flow_rate).toLocaleString()} t/h`} sub="reported windows" />
                    <Cell label="Total delay" value={fmtDuration(delayTotal)} accent={delayTotal > 0 ? "red" : null} />
                </div>

                {/* Reports */}
                <div className="px-5 md:px-10 py-6 md:py-8 border-b border-slate-200">
                    <div className="uppercase-label text-[var(--grindrod-red)] mb-3">2-Hourly Report Timeline</div>
                    <div className="overflow-x-auto -mx-5 md:-mx-10 md:mx-0 px-5 md:px-0">
                        <table className="g-table min-w-[1100px]" data-testid="cert-reports-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Window</th>
                                    <th>TOB</th>
                                    <th>Loaded</th>
                                    <th>Effective h</th>
                                    <th>True rate</th>
                                    <th>vs Target</th>
                                    <th>Lost (t)</th>
                                    <th>Shift / Hatch</th>
                                    <th>Delays</th>
                                    <th>Supervisor</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reports
                                    .filter((r) => r.source !== "hatch-quick-log" && r.source !== "hatch-correction")
                                    .map((r) => {
                                    const eff = Number(r.effective_hours ?? r.window_hours ?? 0);
                                    const trueRate = Number(r.true_flow_rate_tph ?? r.flow_rate_tph ?? 0);
                                    const diff = Number(r.target_diff_pct ?? 0);
                                    const lost = Number(r.lost_tonnage_estimate ?? 0);
                                    const tob = r.draft_figure ?? r.effective_tob ?? r.tob ?? 0;
                                    return (
                                        <React.Fragment key={r.id}>
                                            <tr>
                                                <td className="tabular text-xs">{fmtDate(r.window_start)}</td>
                                                <td className="tabular text-xs whitespace-nowrap">{fmtTime(r.window_start)}–{fmtTime(r.window_end)}</td>
                                                <td className="tabular">{Number(tob).toLocaleString()}</td>
                                                <td className="tabular">{Number(r.tons_loaded).toLocaleString()}</td>
                                                <td className="tabular text-xs">{eff.toFixed(2)} h</td>
                                                <td className="tabular whitespace-nowrap">{Math.round(trueRate).toLocaleString()} t/h</td>
                                                <td className="tabular text-xs">
                                                    <span className={diff >= 0 ? "text-[var(--grindrod-green)] font-bold" : "text-[var(--grindrod-red)] font-bold"}>
                                                        {diff >= 0 ? "+" : ""}{diff.toFixed(0)}%
                                                    </span>
                                                </td>
                                                <td className="tabular text-xs text-[var(--grindrod-red)]">{lost > 0 ? `~${Math.round(lost).toLocaleString()}` : "—"}</td>
                                                <td className="text-xs text-slate-600">
                                                    <span className="capitalize">{r.shift || "—"}</span>
                                                    {r.hatch_no ? ` · H${r.hatch_no}` : ""}
                                                    {r.equipment_used && (
                                                        <div className="text-[10px] text-slate-400">{r.equipment_used}</div>
                                                    )}
                                                </td>
                                                <td className="text-xs">{(r.delays || []).length === 0 ? "—" : `${(r.delays || []).length} (${fmtMins(r.total_delay_minutes)})`}</td>
                                                <td className="text-xs">{r.submitted_by_name}</td>
                                            </tr>
                                            {r.narrative && (
                                                <tr className="bg-slate-50 border-b border-slate-100">
                                                    <td colSpan={11} className="text-[11px] italic text-slate-600 px-3 py-1.5">
                                                        <span className="uppercase-label text-slate-400 mr-2">Summary</span>
                                                        {r.narrative}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Delays */}
                <div className="px-5 md:px-10 py-6 md:py-8 border-b border-slate-200">
                    <div className="uppercase-label text-[var(--grindrod-red)] mb-3">
                        Delay Log · Total {fmtMins(delayTotal)} {delayTotal > 0 && `· ~${Math.round(delays.reduce((s, d) => s + (d.lost_tonnage_est || 0), 0)).toLocaleString()} t lost`}
                    </div>
                    {delays.length === 0 ? (
                        <div className="text-sm text-slate-500">No delays recorded during this operation.</div>
                    ) : (
                        <div className="overflow-x-auto -mx-5 md:-mx-10 md:mx-0 px-5 md:px-0">
                            <table className="g-table min-w-[1080px]" data-testid="cert-delays-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Window</th>
                                        <th>Duration</th>
                                        <th>Severity</th>
                                        <th>Category</th>
                                        <th>Equipment</th>
                                        <th>Reason</th>
                                        <th>Action taken</th>
                                        <th className="text-right pr-3">Lost (t)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {delays.map((d, i) => {
                                        const sev = d.severity || "low";
                                        const sevColor = sev === "critical" ? "text-[var(--grindrod-red)] font-bold"
                                            : sev === "high" ? "text-[var(--grindrod-red)]"
                                            : sev === "medium" ? "text-amber-600"
                                            : "text-slate-500";
                                        return (
                                            <tr key={d.id || i}>
                                                <td className="tabular text-xs">{fmtDate(d.start_time)}</td>
                                                <td className="tabular text-xs whitespace-nowrap">{fmtTime(d.start_time)}–{d.end_time ? fmtTime(d.end_time) : "ongoing"}</td>
                                                <td className="tabular">{fmtMins(d.duration_minutes)}</td>
                                                <td className={`text-xs uppercase font-bold ${sevColor}`}>{sev}</td>
                                                <td className="text-xs capitalize text-slate-600">{d.category || "other"}</td>
                                                <td className="text-xs text-slate-600">{d.equipment_id || "—"}</td>
                                                <td className="text-sm font-semibold text-[var(--grindrod-navy)]">{d.reason}</td>
                                                <td className="text-xs text-slate-600">{d.actions_taken || "—"}</td>
                                                <td className="text-right pr-3 tabular text-xs text-[var(--grindrod-red)]">
                                                    {d.lost_tonnage_est ? `~${Math.round(d.lost_tonnage_est).toLocaleString()}` : "—"}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Manager remarks (print-style) */}
                <RemarksPanel vesselId={vessel.id} printMode />

                {/* Vessel loading overview (per-hatch snapshot at completion) */}
                <div className="px-5 md:px-10 py-6 md:py-8 border-b border-slate-200" data-testid="cert-hatch-section">
                    <HatchOverlayCard vessel={vessel} metrics={metrics} forReport />
                </div>

                {/* Corrections log — audit trail of every hatch-total amendment */}
                <CorrectionsLog reports={reports} />

                {/* ==================== EXPANDED STORY SECTIONS ==================== */}
                {full && <FullStorySections full={full} />}

                {/* Operational Timeline — chronological narrative */}
                <div className="px-5 md:px-10 py-6 md:py-8 border-b border-slate-200" data-testid="cert-timeline-section">
                    <UnifiedTimeline vesselId={vessel.id} printMode />
                </div>

                {/* Footer */}
                <div className="px-5 md:px-10 py-6 md:py-8 flex items-end justify-between gap-4 flex-wrap">
                    <div className="text-xs text-slate-500 max-w-xs">
                        This performance certificate is generated automatically from supervisor-verified 2-hourly reports
                        at Grindrod Terminals · Richards Bay Division.
                    </div>
                    <div className="text-right">
                        <div className="border-t border-slate-400 pt-2 w-56 text-xs text-slate-500">
                            Terminal Manager · Richards Bay
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Cell({ label, value, sub, accent }) {
    const valueColor = accent === "red"
        ? "text-[var(--grindrod-red)]"
        : accent === "green"
            ? "text-[var(--grindrod-green)]"
            : "text-[var(--grindrod-navy)]";
    return (
        <div className="min-w-0">
            <div className="uppercase-label text-[9px]">{label}</div>
            <div className={`font-display font-black ${valueColor} text-base mt-1 break-words`}>{value}</div>
            {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
        </div>
    );
}

/* ============================================================================
   CORRECTIONS LOG — every hatch-total amendment made during the operation.
   Preserves the full audit trail (who, when, prior value, new value, reason)
   so clients see that data integrity is enforced and traceable.
   ============================================================================ */
function CorrectionsLog({ reports }) {
    const corrections = (reports || [])
        .filter((r) => r.source === "hatch-correction")
        .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    if (corrections.length === 0) return null;
    return (
        <div className="px-5 md:px-10 py-6 md:py-8 border-b border-slate-200" data-testid="cert-corrections-section">
            <div className="flex items-center gap-2 mb-3">
                <span className="uppercase-label text-[10px] text-[#B45309]">Corrections log</span>
                <span className="text-xs text-slate-500">{corrections.length} amendment{corrections.length === 1 ? "" : "s"}</span>
            </div>
            <div className="rounded-md border border-[#FED7AA] bg-[#FFFBEB] overflow-hidden">
                <div className="px-4 py-2 border-b border-[#FED7AA] text-[11px] text-[#B45309] leading-relaxed">
                    Every hatch-total amendment recorded during this operation, preserved verbatim.
                    Corrections cannot be silently rewritten — the original entry and the correction are both kept.
                </div>
                <table className="g-table w-full" data-testid="cert-corrections-table">
                    <thead>
                        <tr>
                            <th>When</th>
                            <th>Hatch</th>
                            <th className="text-right">Prior</th>
                            <th className="text-right">New</th>
                            <th className="text-right">Δ</th>
                            <th>Reason</th>
                            <th>By</th>
                        </tr>
                    </thead>
                    <tbody>
                        {corrections.map((c) => {
                            const prior = Number(c.correction_of_prior_total ?? 0);
                            const now = Number(c.corrected_to_total ?? 0);
                            const delta = Number(c.tons_loaded ?? (now - prior));
                            return (
                                <tr key={c.id} data-testid={`cert-correction-row-${c.id}`}>
                                    <td className="text-xs tabular whitespace-nowrap">
                                        {c.created_at ? new Date(c.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                                    </td>
                                    <td className="font-bold text-[var(--grindrod-navy)]">H{c.hatch_no}</td>
                                    <td className="tabular text-right">{prior.toLocaleString()} t</td>
                                    <td className="tabular text-right font-bold text-[var(--grindrod-navy)]">{now.toLocaleString()} t</td>
                                    <td className={`tabular text-right font-bold ${delta > 0 ? "text-[var(--grindrod-green)]" : "text-[var(--grindrod-red)]"}`}>
                                        {delta > 0 ? "+" : ""}{delta.toLocaleString()} t
                                    </td>
                                    <td className="text-xs text-slate-700 italic max-w-[380px]">
                                        {c.correction_reason || c.notes || "—"}
                                    </td>
                                    <td className="text-xs text-slate-600">{c.created_by_name || "—"}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/* ============================================================================
   FULL OPERATION STORY — budget, costs, manpower, fleet, safety, client,
   manager delay feedback, and a roll-call of everyone who touched the vessel.
   Powered by GET /api/vessels/{`id`}/full-report.
   ============================================================================ */
const fmtZAR = (n) => `R ${Number(n || 0).toLocaleString("en-ZA", { maximumFractionDigits: 0 })}`;
const _fmtDateLine = (iso) => iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" }) : "—";

function FullStorySections({ full }) {
    const { budget, costs, manpower, fleet, safety, client_feedback: cfb, delays, participants, tronox } = full || {};
    const totalBudget = budget?.total_budget_zar || 0;
    const variance = totalBudget - (costs?.total_zar || 0);
    const delaysWithFB = (delays || []).filter((d) => d.manager_feedback);
    const topDelays = [...(delays || [])].sort((a, b) => (b.duration_minutes || 0) - (a.duration_minutes || 0)).slice(0, 5);
    const fmtMin = (m) => m == null ? "—" : `${Number(m).toFixed(1)} min`;

    return (
        <>
            {tronox && (
                <div className="px-5 md:px-10 py-6 md:py-8 border-b border-slate-200 bg-sky-50/40" data-testid="cert-tronox-section">
                    <div className="uppercase-label text-[var(--grindrod-red)] mb-3">Tronox Outbound Load-Out · Operations summary</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        <CertStat label="Trucks recorded"      value={(tronox.unique_trucks || 0).toLocaleString()}                   color="#1E40AF" sub={`${(tronox.total_collections || 0).toLocaleString()} visits`} />
                        <CertStat label="Skips loaded"         value={(tronox.total_skips || 0).toLocaleString()}                    color="#B36B00" />
                        <CertStat label="Weighbridge tons"     value={(tronox.truck_total_tons || 0).toLocaleString()}                color="#0EA5E9" sub="Fleet total" />
                        <CertStat label="Avg truck turnaround" value={fmtMin(tronox.fleet_avg_turnaround_minutes)}                    color="#7C3AED" />
                        <CertStat label="Flow rate (net)"      value={`${(tronox.flow_rate_tph || 0).toLocaleString()} t/h`}          color="#00A344" sub="Excl. delays" />
                        <CertStat label="Overall performance"  value={`${(tronox.overall_performance_tph || 0).toLocaleString()} t/h`} color="#7C3AED" sub="Incl. delays" />
                        <CertStat label="Running (net / gross)" value={`${(tronox.running_hours || 0).toFixed(1)} / ${(tronox.gross_running_hours || 0).toFixed(1)} h`} color="var(--grindrod-navy)" sub={`${fmtMin(tronox.paused_minutes)} paused`} />
                        <CertStat label="Conveyor cross-check" value={(tronox.conveyor_total_tons || 0).toLocaleString()}              color="#B36B00" sub="Silo scale tons" />
                    </div>

                    {(tronox.silo_segments || []).length > 0 && (
                        <div className="mb-4">
                            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1">Silos discharged from</div>
                            <table className="g-table text-xs" data-testid="cert-tronox-silo-segments">
                                <thead>
                                    <tr>
                                        <th>Silo</th>
                                        <th>Started</th>
                                        <th>Ended</th>
                                        <th className="text-right">Tons moved (conveyor)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tronox.silo_segments.map((s, i) => (
                                        <tr key={i}>
                                            <td className="font-semibold">Silo {s.silo_no}</td>
                                            <td className="text-slate-500">{_fmtDateLine(s.started_at)}</td>
                                            <td className="text-slate-500">{_fmtDateLine(s.ended_at)}</td>
                                            <td className="text-right tabular font-semibold">{(s.tons_moved_conveyor || 0).toLocaleString()} t</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {(tronox.silo_current_state || []).length > 0 && (
                        <div className="mb-4">
                            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1">Silo levels remaining (post-operation)</div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                {tronox.silo_current_state.map((s) => {
                                    const pct = s.capacity_tons > 0 ? Math.min(100, Math.round((s.current_level_tons / s.capacity_tons) * 100)) : 0;
                                    return (
                                        <div key={s.silo_no} className="bg-white rounded-sm border border-slate-200 p-2" data-testid={`cert-tronox-silo-${s.silo_no}`}>
                                            <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center justify-between">
                                                <span>Silo {s.silo_no}</span>
                                                <span className="text-slate-400 truncate max-w-[60%] text-right">{s.product}</span>
                                            </div>
                                            <div className="font-display font-bold text-[var(--grindrod-navy)] tabular text-sm">{Math.round(s.current_level_tons).toLocaleString()} t</div>
                                            <div className="mt-1 h-1.5 rounded-sm bg-slate-100 overflow-hidden">
                                                <div className="h-full bg-teal-500" style={{ width: `${pct}%` }} />
                                            </div>
                                            <div className="text-[9px] text-slate-500 mt-0.5">{pct}% of {Math.round(s.capacity_tons || 0).toLocaleString()} t capacity</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {(tronox.trucks || []).length > 0 && (
                        <div>
                            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1">Fleet performance · per truck</div>
                            <table className="g-table text-xs" data-testid="cert-tronox-trucks">
                                <thead>
                                    <tr>
                                        <th>Plate</th>
                                        <th className="text-right">Loads</th>
                                        <th className="text-right">Skips</th>
                                        <th className="text-right">Weighbridge tons</th>
                                        <th className="text-right">Avg turnaround</th>
                                        <th className="text-right">Avg on-station</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tronox.trucks.map((t) => (
                                        <tr key={t.plate}>
                                            <td className="font-mono font-semibold">{t.plate}</td>
                                            <td className="text-right tabular">{t.loads}</td>
                                            <td className="text-right tabular">{t.skips}</td>
                                            <td className="text-right tabular">{Number(t.tons || 0).toLocaleString()} t</td>
                                            <td className="text-right tabular text-slate-500">{fmtMin(t.avg_turnaround_minutes)}</td>
                                            <td className="text-right tabular text-slate-500">{fmtMin(t.avg_on_station_minutes)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            <div className="px-5 md:px-10 py-6 md:py-8 border-b border-slate-200" data-testid="cert-cost-section">
                <div className="uppercase-label text-[var(--grindrod-red)] mb-3">Cost Story · Budget vs Actual</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <CertStat label="Total budget" value={fmtZAR(totalBudget)} color="#1E40AF" />
                    <CertStat label="Actual cost"  value={fmtZAR(costs?.total_zar)} color="#B36B00" />
                    <CertStat label="Variance"     value={fmtZAR(variance)} color={variance >= 0 ? "#00A344" : "var(--grindrod-red)"} sub={variance >= 0 ? "under budget" : "over budget"} />
                    <CertStat label="Cost per ton" value={fmtZAR(costs?.cost_per_ton_zar)} color="var(--grindrod-navy)" sub={`${Math.round((costs?.tons_used ?? full.vessel.total_tons_loaded) || 0).toLocaleString()} t loaded`} />
                </div>
                {Object.keys(costs?.by_category || {}).length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                        {Object.entries(costs.by_category).map(([k, v]) => (
                            <div key={k} className="bg-slate-50 rounded-sm px-2 py-1.5 border border-slate-200">
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 capitalize">{k.replace(/_/g, " ")}</div>
                                <div className="font-display font-bold text-[var(--grindrod-navy)] tabular text-sm">{fmtZAR(v)}</div>
                            </div>
                        ))}
                    </div>
                )}
                {(costs?.items || []).length > 0 && (
                    <table className="g-table mt-3 text-xs" data-testid="cert-cost-table">
                        <thead><tr><th>Category</th><th>Description</th><th className="text-right">Amount</th><th>Logged by</th></tr></thead>
                        <tbody>{costs.items.map((c) => (
                            <tr key={c.id}>
                                <td className="capitalize">{(c.category || "").replace(/_/g, " ")}</td>
                                <td>{c.description || "—"}</td>
                                <td className="text-right tabular font-semibold">{fmtZAR(c.amount_zar)}</td>
                                <td className="text-slate-500">{c.logged_by}</td>
                            </tr>))}
                        </tbody>
                    </table>
                )}
            </div>

            <div className="px-5 md:px-10 py-6 md:py-8 border-b border-slate-200" data-testid="cert-manpower-section">
                <div className="uppercase-label text-[var(--grindrod-red)] mb-3">Manpower · Total {manpower?.total_headcount || 0} workers · {(manpower?.total_hours || 0).toFixed(1)} h</div>
                <div className="grid grid-cols-3 gap-3 mb-3">
                    {["electrical","mechanical","operations"].map((d) => (
                        <CertStat key={d} label={d} value={`${manpower?.by_department?.[d]?.headcount || 0} workers`} color="#1E40AF" sub={`${(manpower?.by_department?.[d]?.hours || 0).toFixed(1)} h`} />
                    ))}
                </div>
                {(manpower?.items || []).length > 0 && (
                    <table className="g-table text-xs" data-testid="cert-manpower-table">
                        <thead><tr><th>Shift date</th><th>Dept</th><th className="text-right">Headcount</th><th className="text-right">Hours</th><th>Notes</th><th>Logged by</th></tr></thead>
                        <tbody>{manpower.items.map((m) => (
                            <tr key={m.id}>
                                <td className="tabular">{m.shift_date || fmtDate(m.created_at)}</td>
                                <td className="capitalize">{m.department}</td>
                                <td className="text-right tabular font-semibold">{m.headcount}</td>
                                <td className="text-right tabular">{(m.hours || 0).toFixed(1)}</td>
                                <td className="text-slate-600">{m.notes || "—"}</td>
                                <td className="text-slate-500">{m.logged_by}</td>
                            </tr>))}
                        </tbody>
                    </table>
                )}
                {(manpower?.items || []).length === 0 && <div className="text-sm text-slate-500">No additional manpower captured.</div>}
            </div>

            <div className="px-5 md:px-10 py-6 md:py-8 border-b border-slate-200" data-testid="cert-fleet-section">
                <div className="uppercase-label text-[var(--grindrod-red)] mb-3">Fleet Allocation · {fleet?.machine_count || 0} machine{(fleet?.machine_count || 0) === 1 ? "" : "s"} used</div>
                {(fleet?.items || []).length === 0 ? (
                    <div className="text-sm text-slate-500">No machines allocated or recorded for this vessel.</div>
                ) : (
                    <table className="g-table text-xs" data-testid="cert-fleet-table">
                        <thead><tr><th>Machine</th><th>Make / Model</th><th>Source</th><th>Assigned by</th><th>When</th></tr></thead>
                        <tbody>{fleet.items.map((a) => (
                            <tr key={a.id}>
                                <td className="font-display font-bold text-[var(--grindrod-navy)]">{a.machine_number}</td>
                                <td>{[a.make, a.model].filter(Boolean).join(" ") || "—"}</td>
                                <td><span className={`g-pill ${a.source === "manual" ? "g-pill-navy" : "g-pill-green"}`}>{a.source}</span></td>
                                <td className="text-slate-500">{a.assigned_by || "—"}</td>
                                <td className="text-slate-500 tabular">{_fmtDateLine(a.assigned_at)}</td>
                            </tr>))}
                        </tbody>
                    </table>
                )}
            </div>

            <div className="px-5 md:px-10 py-6 md:py-8 border-b border-slate-200" data-testid="cert-safety-section">
                <div className="uppercase-label text-[var(--grindrod-red)] mb-3">Safety · {safety?.count || 0} incident{(safety?.count || 0) === 1 ? "" : "s"} · {(safety?.total_hours_lost || 0).toFixed(1)} h lost to injury</div>
                {(safety?.items || []).length === 0 ? (
                    <div className="text-sm text-[var(--grindrod-green)]">No safety incidents recorded — zero-harm operation.</div>
                ) : (
                    <table className="g-table text-xs" data-testid="cert-safety-table">
                        <thead><tr><th>When</th><th>Type</th><th>Description</th><th className="text-right">Hrs lost</th><th>Logged by</th></tr></thead>
                        <tbody>{safety.items.map((s) => (
                            <tr key={s.id}>
                                <td className="tabular">{_fmtDateLine(s.occurred_at)}</td>
                                <td className="capitalize">{(s.incident_type || "").replace(/_/g, " ")}</td>
                                <td>{s.description}</td>
                                <td className="text-right tabular">{Number(s.hours_lost || 0).toFixed(1)}</td>
                                <td className="text-slate-500">{s.logged_by}</td>
                            </tr>))}
                        </tbody>
                    </table>
                )}
            </div>

            <div className="px-5 md:px-10 py-6 md:py-8 border-b border-slate-200" data-testid="cert-client-section">
                <div className="uppercase-label text-[var(--grindrod-red)] mb-3">
                    Client Voice · {cfb?.counts?.complaint || 0} complaint{cfb?.counts?.complaint === 1 ? "" : "s"} · {cfb?.counts?.recommendation || 0} recommendation{cfb?.counts?.recommendation === 1 ? "" : "s"} · {cfb?.counts?.positive || 0} positive
                </div>
                {(cfb?.items || []).length === 0 ? (
                    <div className="text-sm text-slate-500">No client feedback recorded.</div>
                ) : (
                    <div className="space-y-2">{cfb.items.map((f) => {
                        const bg = f.feedback_type === "positive" ? "bg-emerald-50 border-emerald-300"
                                 : f.feedback_type === "recommendation" ? "bg-amber-50 border-amber-300"
                                 : "bg-red-50 border-red-300";
                        return (
                            <div key={f.id} className={`border-l-4 rounded-sm p-2.5 ${bg}`}>
                                <div className="flex items-center gap-2 mb-0.5 text-[10px] uppercase tracking-wider">
                                    <span className="font-bold">{f.feedback_type}</span>
                                    {f.category && <span className="text-slate-500">· {f.category}</span>}
                                    <span className="ml-auto text-slate-500">{f.client_name || "—"} · {f.logged_by}</span>
                                </div>
                                <div className="text-sm text-slate-700 whitespace-pre-line">{f.description}</div>
                            </div>
                        );
                    })}</div>
                )}
            </div>

            <div className="px-5 md:px-10 py-6 md:py-8 border-b border-slate-200" data-testid="cert-mgr-feedback-section">
                <div className="uppercase-label text-[var(--grindrod-red)] mb-3">Management Feedback · Top Delays</div>
                {topDelays.length === 0 ? (
                    <div className="text-sm text-slate-500">No delays to comment on.</div>
                ) : (
                    <div className="space-y-2">{topDelays.map((d, i) => (
                        <div key={d.id} className="border border-slate-200 rounded-sm p-2.5">
                            <div className="flex items-center gap-2 flex-wrap text-[11px]">
                                <span className="g-pill g-pill-red">#{i + 1}</span>
                                <span className="font-display font-bold text-[var(--grindrod-navy)]">{d.reason || "Delay"}</span>
                                <span className="text-slate-500">· {fmtDuration(d.duration_minutes || 0)}</span>
                                <span className="text-slate-400 ml-auto">{_fmtDateLine(d.start_time)}</span>
                            </div>
                            {d.actions_taken && <div className="text-xs italic text-slate-600 mt-1">&ldquo;{d.actions_taken}&rdquo;</div>}
                            {d.manager_feedback ? (
                                <div className="mt-1.5 bg-amber-50 border-l-2 border-amber-400 p-2 rounded-sm">
                                    <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Management · {d.manager_feedback_by}</div>
                                    <div className="text-xs text-slate-700 whitespace-pre-line">{d.manager_feedback}</div>
                                </div>
                            ) : (
                                <div className="text-[11px] text-slate-400 italic mt-1">No management feedback yet.</div>
                            )}
                        </div>
                    ))}</div>
                )}
                {delaysWithFB.length > topDelays.filter((d) => d.manager_feedback).length && (
                    <details className="mt-3 text-xs">
                        <summary className="cursor-pointer text-[var(--grindrod-navy)] font-display font-bold uppercase tracking-wide">All delays with feedback ({delaysWithFB.length})</summary>
                        <div className="mt-2 space-y-1.5">{delaysWithFB.map((d) => (
                            <div key={d.id} className="border-l-2 border-amber-400 pl-2">
                                <div className="font-semibold">{d.reason || "Delay"}</div>
                                <div className="text-slate-700 whitespace-pre-line">{d.manager_feedback}</div>
                            </div>))}
                        </div>
                    </details>
                )}
            </div>

            <div className="px-5 md:px-10 py-6 md:py-8 border-b border-slate-200" data-testid="cert-participants-section">
                <div className="uppercase-label text-[var(--grindrod-red)] mb-3">Everyone Involved · {participants?.length || 0} people</div>
                {(participants || []).length === 0 ? (
                    <div className="text-sm text-slate-500">—</div>
                ) : (
                    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
                        {participants.map((p, i) => (
                            <div key={p.name || i} className="bg-slate-50 rounded-sm border border-slate-200 px-2.5 py-1.5">
                                <div className="font-display font-bold text-[var(--grindrod-navy)] text-sm">{p.name}</div>
                                <div className="text-[10px] uppercase tracking-wider text-slate-500">{p.role || "—"} · {p.sources.join(" · ").replace(/_/g, " ")}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
}

function CertStat({ label, value, sub, color }) {
    return (
        <div className="bg-white border border-slate-200 rounded-sm p-3">
            <div className="uppercase-label text-[9.5px] text-slate-500">{label}</div>
            <div className="font-display font-black tabular text-base md:text-lg mt-0.5" style={{ color }}>{value}</div>
            {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
        </div>
    );
}
